import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { getBackendMode, loadBackend } from "../../services/backend";
import {
  ensureUserProfile,
  subscribeToProfile,
  touchLastActive,
} from "../../services/users/userService";
import { friendlyAuthError } from "../../utils/authErrors";

/**
 * Authentication state for the whole application.
 *
 * status:
 *   "initializing": a backend-required route is checking the session
 *   "signed-out":   no session; the root route sends the user to /welcome
 *   "signed-in":    authUser and profile are both available
 *   "unavailable":  the build has no usable backend (missing Firebase config)
 *
 * Public routes do not force this check before they render. They show the
 * signed-out surface immediately and ask for auth only after first paint,
 * so a new visitor does not pay for Firebase before reading the landing page.
 */
export const AuthContext = createContext(null);

const initialState = {
  status: "signed-out",
  authUser: null,
  profile: null,
  error: null,
};

export function AuthProvider({ children }) {
  const backendMode = useMemo(() => getBackendMode(), []);

  // The Firebase SDK lives in its own chunk. Keep it out of the public
  // landing path until a route or action actually needs auth.
  const [backend, setBackend] = useState(null);
  const [state, setState] = useState(initialState);
  const [pendingAction, setPendingAction] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);

  const backendRef = useRef(null);
  const backendLoadRef = useRef(null);

  useEffect(() => {
    backendRef.current = backend;
  }, [backend]);

  const ensureAuthReady = useCallback(async () => {
    if (backendRef.current) return backendRef.current;
    if (backendLoadRef.current) return backendLoadRef.current;

    setAuthLoading(true);
    setState((prev) =>
      authReady ? prev : { ...prev, status: "initializing", error: null }
    );

    backendLoadRef.current = loadBackend()
      .then((next) => {
        if (!next) {
          setState({ ...initialState, status: "unavailable" });
          setAuthReady(true);
          return;
        }
        setBackend(() => next);
        return next;
      })
      .catch(() => {
        setState({ ...initialState, status: "unavailable" });
        setAuthReady(true);
        return null;
      })
      .finally(() => {
        setAuthLoading(false);
        backendLoadRef.current = null;
      });

    return backendLoadRef.current;
  }, [authReady]);

  useEffect(() => {
    if (backendMode.mode === "local") ensureAuthReady();
  }, [backendMode.mode, ensureAuthReady]);

  // Guards against setState after unmount during slow popups.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /* ---- react to auth changes ---- */
  useEffect(() => {
    if (!backend) return undefined;

    let profileUnsubscribe = null;

    const stopProfileSubscription = () => {
      if (profileUnsubscribe) {
        profileUnsubscribe();
        profileUnsubscribe = null;
      }
    };

    const unsubscribe = backend.onAuthStateChange(async (authUser) => {
      stopProfileSubscription();

      if (!authUser) {
        if (mounted.current) {
          setState({ ...initialState, status: "signed-out" });
          setAuthReady(true);
        }
        return;
      }

      try {
        const profile = await ensureUserProfile(authUser);
        if (!mounted.current) return;

        setState({ status: "signed-in", authUser, profile, error: null });
        setAuthReady(true);
        touchLastActive(authUser.uid);

        // Keep IQ, concepts learned etc. live without re-reading on every render.
        profileUnsubscribe = subscribeToProfile(authUser.uid, (next) => {
          if (next && mounted.current) {
            setState((prev) => ({ ...prev, profile: next }));
          }
        });
      } catch (error) {
        if (!mounted.current) return;
        setAuthReady(true);
        setState({
          status: "signed-in",
          authUser,
          profile: null,
          error: {
            message:
              "We signed you in, but couldn't load your profile. Your progress may not save.",
            detail: error,
          },
        });
      }
    });

    return () => {
      stopProfileSubscription();
      unsubscribe();
    };
  }, [backend]);

  /* ---- actions ---- */
  const run = useCallback(
    async (name, fn) => {
      setPendingAction(name);
      setState((prev) => ({ ...prev, error: null }));
      try {
        const readyBackend = backend ?? (await ensureAuthReady());
        if (!readyBackend) return null;
        return await fn(readyBackend);
      } catch (error) {
        if (mounted.current) {
          setState((prev) => ({
            ...prev,
            error: { message: friendlyAuthError(error), detail: error },
          }));
        }
        return null;
      } finally {
        if (mounted.current) setPendingAction(null);
      }
    },
    [backend, ensureAuthReady]
  );

  const continueAsGuest = useCallback(
    () => run("guest", (readyBackend) => readyBackend.signInAsGuest()),
    [run]
  );

  const signInWithGoogle = useCallback(
    () => run("google", (readyBackend) => readyBackend.signInWithGoogle()),
    [run]
  );

  const upgradeAccount = useCallback(
    () =>
      run("upgrade", (readyBackend) => readyBackend.upgradeGuestWithGoogle()),
    [run]
  );

  const signOut = useCallback(
    () => run("signout", (readyBackend) => readyBackend.signOut()),
    [run]
  );

  const clearError = useCallback(
    () => setState((prev) => ({ ...prev, error: null })),
    []
  );

  const value = useMemo(
    () => ({
      ...state,
      backendMode,
      isLocalMode: backendMode.mode === "local",
      isGuest: Boolean(state.authUser?.isAnonymous),
      authReady,
      authLoading,
      pendingAction,
      ensureAuthReady,
      continueAsGuest,
      signInWithGoogle,
      upgradeAccount,
      signOut,
      clearError,
    }),
    [
      state,
      backendMode,
      authReady,
      authLoading,
      pendingAction,
      ensureAuthReady,
      continueAsGuest,
      signInWithGoogle,
      upgradeAccount,
      signOut,
      clearError,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
